import { useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useOptimisticSharedItemsCommunicationsDeleteCacheUpdate, useOptimisticSharedItemsCommunicationSeenCacheUpdate, useOptimisticSharedItemsDocumentsCopyCacheUpdate } from '../shared';
import { useOptimisticCommunicationDocumentsCopyCacheUpdate, useOptimisticCommunicationsDocumentsCopyCacheUpdate } from './cache';
import CommunicationsQueryKeys from './CommunicationsQueryKeys';
import { mapCommunicationsToEntities, mapCommunicationToEntity } from './helpers';
const communicationsQueryKeys = new CommunicationsQueryKeys();
const createCommunicationsModule = ({ communicationsApi, }) => {
    const useCreateCommunications = () => useMutation({
        mutationFn: ({ userIds, ...params }) => Promise.allSettled(userIds.map(receiver => communicationsApi.createCommunication(({ ...params, receiver })))),
    });
    const useCreateCommunication = () => useMutation({
        mutationFn: params => communicationsApi.createCommunication(params),
    });
    const useGetCommunications = ({ enabled = true, pageSize = 15, params, } = {}) => {
        const { data, isLoading, error, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
            queryKey: communicationsQueryKeys.getCommunications(params),
            queryFn: ({ pageParam }) => communicationsApi.getCommunications({
                limit: pageSize,
                offset: Number(pageParam),
                ...params,
            }),
            enabled,
            initialPageParam: 0,
            getNextPageParam: (lastPage, allPages) => {
                const totalFetchedCommunications = allPages.flatMap(page => page.communications).length;
                return totalFetchedCommunications < lastPage.total ?
                    allPages.length * pageSize :
                    undefined;
            },
        });
        const allCommunications = useMemo(() => mapCommunicationsToEntities(data?.pages.flatMap(page => page.communications)), [data?.pages]);
        return {
            communications: allCommunications,
            total: data?.pages[0]?.total ?? 0,
            error,
            isLoading,
            refetch,
            hasNextPage,
            fetchNextPage,
            isFetchingNextPage,
        };
    };
    const useGetCommunication = ({ communicationId, markAsSeen = true }) => {
        const { markSharedItemCommunicationAsSeen } = useOptimisticSharedItemsCommunicationSeenCacheUpdate();
        const { data, isLoading, error, refetch } = useQuery({
            queryKey: communicationsQueryKeys.getCommunication(communicationId ?? ''),
            queryFn: async () => {
                const res = await communicationsApi.getCommunication(communicationId ?? '');
                if (markAsSeen && communicationId) {
                    markSharedItemCommunicationAsSeen({ communicationId }); // Automatically set by the API;
                }
                return mapCommunicationToEntity(res);
            },
            enabled: Boolean(communicationId),
        });
        return {
            communication: data,
            isLoading,
            error,
            refetch,
        };
    };
    const useCopyCommunicationDocuments = () => {
        const { onMutate: handleSharedItemsMutation, onError: handleSharedItemsMutationError } = useOptimisticSharedItemsDocumentsCopyCacheUpdate();
        const { onMutate: handleCommunicationsMutation, onError: handleCommunicationsMutationError } = useOptimisticCommunicationsDocumentsCopyCacheUpdate();
        const { onMutate: handleCommunicationMutation, onError: handleCommunicationMutationError } = useOptimisticCommunicationDocumentsCopyCacheUpdate();
        const { mutateAsync, isPending, error } = useMutation({
            mutationFn: ({ communicationId, documentIds, }) => communicationsApi.copyCommunicationDocuments(communicationId, documentIds),
            onMutate: async (args) => ({
                ...await handleSharedItemsMutation(args),
                ...await handleCommunicationsMutation(args),
                ...await handleCommunicationMutation(args),
            }),
            onError: (error, variable, context) => {
                handleSharedItemsMutationError(error, variable, context);
                handleCommunicationsMutationError(error, variable, context);
                handleCommunicationMutationError(error, variable, context);
            },
        });
        return {
            copyCommunicationDocuments: mutateAsync,
            isPending,
            error,
        };
    };
    const useDownloadCommunicationDocument = () => {
        const { mutateAsync, isPending, error } = useMutation({
            mutationFn: ({ communicationId, documentId }) => communicationsApi.downloadCommunicationDocument(communicationId, documentId),
        });
        return {
            downloadCommunicationDocument: mutateAsync,
            error,
            isPending,
        };
    };
    const useDeleteCommunications = () => {
        const { onMutate, onError, onSuccess } = useOptimisticSharedItemsCommunicationsDeleteCacheUpdate();
        const { mutateAsync, isPending, error } = useMutation({
            mutationFn: ({ communicationIds }) => communicationsApi.deleteCommunications(communicationIds),
            onMutate,
            onError,
            onSuccess,
        });
        return {
            deleteCommunications: mutateAsync,
            error,
            isPending,
        };
    };
    return {
        useDeleteCommunications,
        useCreateCommunication,
        useCreateCommunications,
        useGetCommunications,
        useGetCommunication,
        useCopyCommunicationDocuments,
        useDownloadCommunicationDocument,
    };
};
export default createCommunicationsModule;
